import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  subscribeToPeriodUpdates,
  subscribeToScheduleVariants,
  getCurrentPeriodState,
  getScheduleVariants,
} from '../utils/realTimeScheduleUtils';
import {
  getPeriodNumber,
  getPeriodDisplayName,
  type ABDay,
  type PeriodSlot,
} from '../utils/scheduleUtils';
import { useAuth } from './AuthContext';

// The org context slice this provider consumes: only currentOrgId is read
// (OrganizationContext resolves it to String(currentUser.school.id)).
export interface OrgContextValue {
  currentOrgId?: string | number | null;
}

// The derived, fully-shaped period object getCurrentPeriod hands to consumers.
export interface CurrentPeriod {
  name: string | null;
  displayName: string;
  periodNumber: PeriodSlot | null;
  abDay: ABDay | string | null;
  scheduleType: string;
  timeRemaining: number;
  start: string;
  end: string;
  isInSession: boolean;
  source: 'backend';
}

// The value exposed through DevOverrideContext.
export interface DevOverrideContextValue {
  isAutoMode: boolean;
  isDeveloperMode: boolean;
  updateTrigger: number;
  getCurrentPeriod: () => CurrentPeriod | null;
  getScheduleType: () => string | null;
  getABDay: () => ABDay | string | null;
  scheduleVariantsMap: Record<string, string>;
}

export interface DevOverrideProviderProps {
  children: React.ReactNode;
  orgContextValue?: OrgContextValue | null;
}

export const DevOverrideContext = createContext<
  DevOverrideContextValue | undefined
>(undefined);

export const useDevOverride = (): DevOverrideContextValue => {
  const context = useContext(DevOverrideContext);
  if (!context) {
    throw new Error('useDevOverride must be used within a DevOverrideProvider');
  }
  return context;
};

// Manual period-override (the 5 Firestore listeners + 5 httpsCallable calls
// backing developer-only manual override) has no hallpass-backend REST
// equivalent and was dropped per product decision -- DeveloperToolsPanel.js
// is gone and isAutoMode/isDeveloperMode below are fixed constants.
//
// Auto-mode period/schedule/AB-day detection IS restored here though: it was
// NOT actually blocked on missing backend support. OrganizationContext.js
// already resolves a real currentOrgId (String(currentUser.school.id)), and
// realTimeScheduleUtils.js (built earlier in this same migration) already
// exposes subscribeToPeriodUpdates and subscribeToScheduleVariants, both of
// which share a single REST poller per org (orgPollers in
// realTimeScheduleUtils.js) hitting GET /schedule/today on a 30s interval --
// there is no Firestore listener involved for either subscription anymore --
// plus synchronous accessors (getCurrentPeriodState/getScheduleVariants)
// over the same module-level cache those subscriptions populate.
//
// getCurrentPeriod/getScheduleType/getABDay read that shared cache LIVE on
// every call (rather than from a locally-copied snapshot) so they stay
// correct even though StudentDashboard.js separately calls
// subscribeToPeriodUpdates/subscribeToScheduleVariants itself for the same
// org (pre-existing, not part of this fix) -- realTimeScheduleUtils.js
// normalizes orgId to String(orgId) internally before every Map
// get/set/has/delete, so this context's currentOrgId (a string, from
// OrganizationContext.js) and StudentDashboard.js's schoolId (a number,
// the same underlying org) both key into the identical cache entry
// regardless of which type either caller passes in -- whichever listener
// is currently alive keeps it fresh.
export const DevOverrideProvider = ({
  children,
  orgContextValue = null,
}: DevOverrideProviderProps) => {
  const { currentUser, userRole } = useAuth();
  const currentOrgId: string | number | null =
    orgContextValue?.currentOrgId || null;

  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    if (!currentUser || !userRole || !currentOrgId) return;

    const unsubscribePeriod = subscribeToPeriodUpdates(currentOrgId, () => {
      setUpdateTrigger((prev) => prev + 1);
    });

    const unsubscribeVariants = subscribeToScheduleVariants(
      currentOrgId,
      () => {
        setUpdateTrigger((prev) => prev + 1);
      }
    );

    return () => {
      unsubscribePeriod();
      unsubscribeVariants();
    };
  }, [currentUser, userRole, currentOrgId]);

  const getCurrentPeriod = useCallback((): CurrentPeriod | null => {
    const periodState = getCurrentPeriodState(currentOrgId);
    if (!periodState) return null;

    return {
      name: periodState.period,
      displayName: periodState.period
        ? getPeriodDisplayName(periodState.period, periodState.abDay)
        : 'Unknown',
      periodNumber: periodState.period
        ? getPeriodNumber(periodState.period, periodState.abDay)
        : null,
      abDay: periodState.abDay,
      scheduleType: periodState.scheduleType || 'unknown',
      timeRemaining: periodState.periodInfo?.timeRemaining || 0,
      start: periodState.periodInfo?.start || '00:00',
      end: periodState.periodInfo?.end || '23:59',
      isInSession: periodState.isInSession || false,
      source: 'backend',
    };
  }, [currentOrgId]);

  const getScheduleType = useCallback((): string | null => {
    return getCurrentPeriodState(currentOrgId)?.scheduleType || null;
  }, [currentOrgId]);

  const getABDay = useCallback((): ABDay | string | null => {
    return getCurrentPeriodState(currentOrgId)?.abDay || null;
  }, [currentOrgId]);

  const scheduleVariantsMap = useMemo<Record<string, string>>(() => {
    const variants = getScheduleVariants(currentOrgId);
    const map: Record<string, string> = {};
    Object.values(variants).forEach((variant) => {
      if (variant?.id != null && variant?.name) {
        map[variant.id] = variant.name;
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, updateTrigger]);

  const value: DevOverrideContextValue = {
    isAutoMode: true,
    isDeveloperMode: false,
    updateTrigger,
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    scheduleVariantsMap,
  };

  return (
    <DevOverrideContext.Provider value={value}>
      {children}
    </DevOverrideContext.Provider>
  );
};
